"use client";

import React, { useId } from "react";
import { PhoneIcon } from "lucide-react";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import { cn } from "../lib/utils";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger } from "./select";
import { platform } from "../lib/platform";
import z from "zod";
import { f9jaPhone } from "../lib/utils/format";
interface PhoneInputProps {
  value?: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  label?: string;
}

export default function PhoneInput({
  value,
  onChange,
  placeholder = "Enter phone number",
  disabled,
  className,
  id: providedId,
  label,
}: PhoneInputProps) {
  const generatedId = useId();
  const id = providedId || generatedId;

  return (
    <div className={cn("*:not-first:mt-2")} dir="ltr">
      {label && <Label htmlFor={id}>{label}</Label>}
      <RPNInput.default
        className={cn("flex rounded-md shadow-xs", className)}
        international={!platform.isFarmer}
        defaultCountry={platform.isFarmer ? "NG" : undefined}
        countries={platform.isFarmer ? ["NG"] : undefined}
        flagComponent={FlagComponent}
        countrySelectComponent={CountrySelect}
        inputComponent={InputField}
        id={id}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    </div>
  );
}

const InputField = ({ className, ...props }: React.ComponentProps<"input">) => (
  <Input
    data-slot="phone-input"
    className={cn(
      "-ms-px rounded-s-none shadow-none focus-visible:z-10 border-0 border-l",
      "h-12 bg-white font-poppins font-medium placeholder:text-[#C1C1C1] text-xs md:text-xs",
      className,
    )}
    {...props}
  />
);

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  onChange: (value: RPNInput.Country) => void;
  options: { label: string; value: RPNInput.Country | undefined }[];
};

const CountrySelect = ({
  disabled,
  value,
  onChange,
  options,
}: CountrySelectProps) => {
  const normalizedOptions = options
    .filter((x) => x.value)
    .map((option) => ({
      value: option.value as string,
      label: `${option.label} ${
        option.value ? `+${RPNInput.getCountryCallingCode(option.value)}` : ""
      }`,
    }));

  return (
    <Select
      value={value}
      onValueChange={(val) => onChange(val as RPNInput.Country)}
      disabled={disabled}
    >
      <SelectTrigger className="w-auto border-none border-r rounded-s-md rounded-e-none px-3 gap-1">
        <FlagComponent country={value} countryName={value} />
      </SelectTrigger>
      <SelectContent>
        {normalizedOptions.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country];
  return (
    <span className="w-5 overflow-hidden rounded-sm">
      {Flag ? (
        <Flag title={countryName} />
      ) : (
        <PhoneIcon size={16} aria-hidden="true" />
      )}
    </span>
  );
};

export const phoneDataSchema = platform.isFarmer
  ? z
      .string({ error: "Enter a valid phone number" })
      .min(1, { message: "Phone number is required" })
      .transform(f9jaPhone)
      .pipe(
        z.string().regex(/^\+234\d{10}$/, {
          message: "Phone number must be 10 digits (or 11 starting with 0)",
        }),
      )
  : z
      .string({ error: "Enter a valid phone number" })
      .min(1, "Phone number is required")
      .refine((val) => /^\+[1-9]\d{1,14}$/.test(val), "Invalid phone number");
